/**
 * RKHLocationModal Component
 * Modal for selecting and registering locations/blocks for RKH
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Checkbox,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Badge,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useToast,
    Divider,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Icon,
} from '@chakra-ui/react';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { useRKHLocation } from '../hooks/useRKHLocation';
import { LocationInput } from '../types';

interface RKHLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    rkhId: string;
    rkhNumber: string;
    userEmail: string;
    onSuccess?: () => void;
}

interface LocationFormData extends LocationInput {
    selected: boolean;
    plantedArea: number;
    hasTaksasiLast: boolean;
    hasTaksasiTarget: boolean;
    /** Date string of last_taksasi, shown when carry over is active */
    lastTaksasiDate: string | null;
    /** True if this block was already registered in the RKH (read-only) */
    isPreSelected: boolean;
}

const RKHLocationModal: React.FC<RKHLocationModalProps> = ({
    isOpen,
    onClose,
    rkhId,
    rkhNumber,
    userEmail,
    onSuccess,
}) => {
    const toast = useToast();
    const { availableBlocks, rkhInfo, loading, error, submitting, fetchAvailableBlocks, registerLocations, clearData } = useRKHLocation();
    
    const [formData, setFormData] = useState<LocationFormData[]>([]);

    // Fetch available blocks when modal opens
    useEffect(() => {
        if (isOpen && rkhId) {
            fetchAvailableBlocks(rkhId);
        }
    }, [isOpen, rkhId, fetchAvailableBlocks]);

    // Initialize form data when blocks are loaded
    useEffect(() => {
        if (availableBlocks.length > 0) {
            const initialData: LocationFormData[] = availableBlocks.map((block) => ({
                block_id: block.block_id,
                block_code: block.block_code,
                // Auto carry-over: no target taksasi but last taksasi exists
                is_carry_over: !block.target_date_taksasi.exists && block.last_taksasi.exists,
                harvest_area_target: 0,
                hanca_cnt: 0,
                // Pre-check blocks already registered for this RKH
                selected: block.is_selected,
                plantedArea: block.planted_area,
                hasTaksasiLast: block.last_taksasi.exists,
                hasTaksasiTarget: block.target_date_taksasi.exists,
                lastTaksasiDate: block.last_taksasi.date ?? null,
                isPreSelected: block.is_selected,
            }));
            setFormData(initialData);
        }
    }, [availableBlocks]);

    const handleCheckboxChange = useCallback((index: number, checked: boolean) => {
        setFormData((prev) => {
            const newData = [...prev];
            newData[index] = { ...newData[index], selected: checked };
            return newData;
        });
    }, []);

    const handleCarryOverChange = useCallback((index: number, checked: boolean) => {
        setFormData((prev) => {
            const newData = [...prev];
            newData[index] = { ...newData[index], is_carry_over: checked };
            return newData;
        });
    }, []);

    const handleHarvestAreaChange = useCallback((index: number, value: string) => {
        const numValue = Number.parseFloat(value) || 0;
        setFormData((prev) => {
            const newData = [...prev];
            newData[index] = { ...newData[index], harvest_area_target: numValue };
            return newData;
        });
    }, []);

    const handleHancaCountChange = useCallback((index: number, value: string) => {
        const numValue = Number.parseInt(value, 10) || 0;
        setFormData((prev) => {
            const newData = [...prev];
            newData[index] = { ...newData[index], hanca_cnt: numValue };
            return newData;
        });
    }, []);

    const handleSubmit = async () => {
        const selectedLocations = formData.filter((item) => item.selected);

        if (selectedLocations.length === 0) {
            toast({
                title: 'Validasi Gagal',
                description: 'Pilih minimal satu blok untuk disimpan',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Validate harvest area and hanca count
        const invalidItems = selectedLocations.filter(
            (item) => item.harvest_area_target <= 0 || item.hanca_cnt <= 0
        );

        if (invalidItems.length > 0) {
            toast({
                title: 'Validasi Gagal',
                description: 'Luas Panen Target dan Jumlah Hanca harus lebih dari 0',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const locationsToSubmit: LocationInput[] = selectedLocations.map((item) => ({
            block_id: item.block_id,
            block_code: item.block_code,
            is_carry_over: item.is_carry_over,
            harvest_area_target: item.harvest_area_target,
            hanca_cnt: item.hanca_cnt,
        }));

        const success = await registerLocations(rkhId, locationsToSubmit, userEmail);

        if (success) {
            toast({
                title: 'Berhasil',
                description: 'Lokasi berhasil disimpan',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            handleClose();
            if (onSuccess) {
                onSuccess();
            }
        } else {
            toast({
                title: 'Gagal',
                description: error || 'Gagal menyimpan lokasi',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleClose = () => {
        setFormData([]);
        clearData();
        onClose();
    };

    const selectedCount = formData.filter((item) => item.selected).length;
    const totalHarvestArea = formData
        .filter((item) => item.selected)
        .reduce((sum, item) => sum + item.harvest_area_target, 0);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <VStack align="stretch" spacing={1}>
                        <Text>Edit Lokasi RKH</Text>
                        <Text fontSize="sm" fontWeight="normal" color="gray.600">
                            {rkhNumber}
                        </Text>
                        {rkhInfo && (
                            <HStack fontSize="xs" color="gray.500" spacing={3} flexWrap="wrap">
                                <Text>Tanggal: <strong>{rkhInfo.rkh_date}</strong></Text>
                                <Text>Estate: <strong>{rkhInfo.estate_name}</strong></Text>
                                <Text>Divisi: <strong>{rkhInfo.division_name}</strong></Text>
                            </HStack>
                        )}
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />
                
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {loading && (
                            <VStack py={10} spacing={4}>
                                <Spinner size="xl" color="blue.500" />
                                <Text color="gray.600">Memuat data blok...</Text>
                            </VStack>
                        )}
                        
                        {!loading && error && (
                            <Alert status="error" borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>Gagal memuat data</AlertTitle>
                                    <AlertDescription>{error}</AlertDescription>
                                </Box>
                            </Alert>
                        )}
                        
                        {!loading && !error && formData.length === 0 && (
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <AlertDescription>Tidak ada blok tersedia</AlertDescription>
                            </Alert>
                        )}
                        
                        {!loading && !error && formData.length > 0 && (
                            <>
                                {/* Summary Cards */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box p={4} bg="blue.50" borderRadius="md">
                                        <Stat>
                                            <StatLabel>Blok Dipilih</StatLabel>
                                            <StatNumber color="blue.600">{selectedCount}</StatNumber>
                                            <StatHelpText>dari {formData.length} blok tersedia</StatHelpText>
                                        </Stat>
                                    </Box>
                                    <Box p={4} bg="green.50" borderRadius="md">
                                        <Stat>
                                            <StatLabel>Total Luas Panen Target</StatLabel>
                                            <StatNumber color="green.600">
                                                {totalHarvestArea.toFixed(2)} Ha
                                            </StatNumber>
                                            <StatHelpText>dari blok yang dipilih</StatHelpText>
                                        </Stat>
                                    </Box>
                                </SimpleGrid>

                                <Divider />

                                {/* Table */}
                                <Box overflowX="auto">
                                    <Table variant="simple" size="sm">
                                        <Thead bg="gray.100">
                                            <Tr>
                                                <Th width="50px">Pilih</Th>
                                                <Th>Kode Blok</Th>
                                                <Th isNumeric>Luas Tanam (Ha)</Th>
                                                <Th textAlign="center">Taksasi</Th>
                                                <Th textAlign="center">Carry Over</Th>
                                                <Th isNumeric>Luas Panen Target (Ha)</Th>
                                                <Th isNumeric>Jumlah Hanca</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {formData.map((item, index) => (
                                                <Tr key={item.block_id} bg={item.selected ? 'blue.50' : 'white'}>
                                                        <Td>
                                                            {item.isPreSelected ? (
                                                                <Checkbox
                                                                    isChecked
                                                                    isReadOnly
                                                                    colorScheme="gray"
                                                                    title="Blok ini sudah terdaftar di RKH"
                                                                />
                                                            ) : (
                                                                <Checkbox
                                                                    isChecked={item.selected}
                                                                    onChange={(e) =>
                                                                        handleCheckboxChange(index, e.target.checked)
                                                                    }
                                                                />
                                                            )}
                                                        </Td>
                                                        <Td>
                                                            <HStack spacing={2}>
                                                                <Text fontWeight="bold">{item.block_code}</Text>
                                                                {item.isPreSelected && (
                                                                    <Badge colorScheme="purple" fontSize="xs" variant="subtle">
                                                                        Terdaftar
                                                                    </Badge>
                                                                )}
                                                            </HStack>
                                                        </Td>
                                                        <Td isNumeric>{item.plantedArea.toFixed(2)}</Td>
                                                        <Td>
                                                            {item.hasTaksasiTarget ? (
                                                                <Badge colorScheme="green" fontSize="xs">
                                                                    <HStack spacing={1}>
                                                                        <Icon as={FiCheckCircle} />
                                                                        <Text>Taksasi</Text>
                                                                    </HStack>
                                                                </Badge>
                                                            ) : (
                                                                <Badge colorScheme="red" fontSize="xs">
                                                                    <HStack spacing={1}>
                                                                        <Icon as={FiAlertCircle} />
                                                                        <Text>Tidak Ada Taksasi</Text>
                                                                    </HStack>
                                                                </Badge>
                                                            )}
                                                        </Td>
                                                        <Td textAlign="center">
                                                            <VStack spacing={1} align="center">
                                                                <Checkbox
                                                                    isChecked={item.is_carry_over}
                                                                    onChange={(e) =>
                                                                        handleCarryOverChange(index, e.target.checked)
                                                                    }
                                                                    isDisabled={!item.selected}
                                                                />
                                                                {item.is_carry_over && item.lastTaksasiDate && (
                                                                    <Text fontSize="xs" color="blue.600" fontWeight="medium">
                                                                        {item.lastTaksasiDate}
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <NumberInput
                                                                value={item.harvest_area_target}
                                                                onChange={(val) =>
                                                                    handleHarvestAreaChange(index, val)
                                                                }
                                                                min={0}
                                                                max={item.plantedArea}
                                                                step={0.1}
                                                                precision={2}
                                                                isDisabled={!item.selected}
                                                                size="sm"
                                                            >
                                                                <NumberInputField />
                                                                <NumberInputStepper>
                                                                    <NumberIncrementStepper />
                                                                    <NumberDecrementStepper />
                                                                </NumberInputStepper>
                                                            </NumberInput>
                                                        </Td>
                                                        <Td>
                                                            <NumberInput
                                                                value={item.hanca_cnt}
                                                                onChange={(val) =>
                                                                    handleHancaCountChange(index, val)
                                                                }
                                                                min={0}
                                                                step={1}
                                                                isDisabled={!item.selected}
                                                                size="sm"
                                                            >
                                                                <NumberInputField />
                                                                <NumberInputStepper>
                                                                    <NumberIncrementStepper />
                                                                    <NumberDecrementStepper />
                                                                </NumberInputStepper>
                                                            </NumberInput>
                                                        </Td>
                                                    </Tr>
                                                )
                                            )}
                                        </Tbody>
                                    </Table>
                                </Box>

                                {/* Info Box */}
                                <Alert status="info" borderRadius="md" fontSize="sm">
                                    <AlertIcon />
                                    <Box>
                                        <AlertDescription>
                                            <VStack align="start" spacing={1}>
                                                <Text>
                                                    <strong>Carry Over:</strong> Centang jika menggunakan data
                                                    taksasi terakhir yang tersedia (otomatis dicentang jika
                                                    tidak ada taksasi untuk tanggal target)
                                                </Text>
                                                <Text>
                                                    <strong>Luas Panen Target:</strong> Masukkan luas area yang
                                                    akan dipanen (maksimal sesuai luas tanam)
                                                </Text>
                                                <Text>
                                                    <strong>Jumlah Hanca:</strong> Masukkan jumlah hanca/pemanen
                                                    yang akan ditugaskan
                                                </Text>
                                            </VStack>
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            </>
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3}>
                        <Button variant="ghost" onClick={handleClose} isDisabled={submitting}>
                            Batal
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleSubmit}
                            isLoading={submitting}
                            loadingText="Menyimpan..."
                            isDisabled={loading || selectedCount === 0}
                        >
                            Simpan Lokasi
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default RKHLocationModal;
